import type { HomePageType } from '@/domains/home/apis/home-api';
import { NAV_MENU_TYPE } from '@eureka/shared/nav-bar';
import { E_ShareType } from '@eureka/services/src/types/share';
import { AuthService } from '@eureka/shared/share-tools/logic';
import { accountDeferred, addTrialDeferred, isUserLogin } from '@eureka/utils/account';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { createRouter, createWebHistory } from 'vue-router';
import { proxyRouterList } from './proxy-router';
import { deprecatedRouter } from './deprecated-router';
import { ipRouterList } from './ip-router';
import { HomeService } from '@/domains/home/services/home-service';
import { SiteRegion } from '@eureka/services/src/types/region';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/rd-search',
            name: 'rd-search',
            component: () => import('@/pages/home/components/rd-search-section.vue'),
        },
        {
            path: '/',
            component: () => import('@/pages/Layout.vue'),
            children: [
                deprecatedRouter,
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/pages/home/new-home.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                {
                    path: '/rd-home',
                    name: 'rd-home',
                    component: () => import('@/pages/home/new-home.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                {
                    path: '/subscribe',
                    name: 'subscribe',
                    component: () => import('@/pages/home/CreatePayment.vue'),
                },
                {
                    path: '/new-home',
                    name: 'new-home',
                    component: () => import('@/pages/home/new-home.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                {
                    path: '/rd-gallery',
                    name: 'rd-gallery',
                    component: () => import('@/pages/gallery/index.vue'),
                },
                {
                    path: '/check-payment-status',
                    name: 'check-payment-status',
                    component: () => import('@/pages/home/CheckPaymentStatus.vue'),
                },
                {
                    path: '/ls',
                    name: 'ls',
                    component: () => import('@/pages/home/LS.vue'),
                    beforeEnter: async(to, from, next) => {
                        const searchParams = new URLSearchParams(location.search)
                        location.href = `/ls/#/enterprise${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`
                        next(false)
                    },
                },
                {
                    path: '/ls/home',
                    name: 'ls-home',
                    component: () => import('@/pages/home/LSHome.vue'),
                    beforeEnter: async(to, from, next) => {
                        const searchParams = new URLSearchParams(location.search)
                        location.href = `/ls/#/home${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`
                        next(false)
                    },
                },
                {
                    path: '/mse',
                    name: 'mse',
                    component: () => import('@/pages/home/MSE.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        window.location.href = '/mse/#/home'
                        next(false);
                    },
                },
                ...ipRouterList,
                {
                    path: '/explore',
                    name: 'explore',
                    component: () => import('@/pages/ai/Explore.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CUSTOM_AGENT,
                    },
                },
                {
                    path: '/user-profile',
                    name: 'user-profile',
                    component: () => import('@/pages/UserProfile.vue'),
                },
                {
                    name: 'seek',
                    path: '/page/:seek_id',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.seek_id as string,
                            data_type: E_ShareType.SEEK_SEARCH,
                        });
                        next();
                    },
                    component: () => import('@/pages/seek/Page.vue'),
                },
                {
                    name: 'generalSearch',
                    path: '/general-search',
                    component: () => import('@/pages/general-search/index.vue'),
                },
                {
                    name: 'materialsGeneralSearch',
                    path: '/materials-general-search',
                    component: () => import('@/pages/materials-general-search/index.vue'),
                },
                {
                    name: 'ai-ls',
                    path: '/ai-ls',
                    children: [
                        {
                            path: ':lsAgentName',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-entry',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lsAgentName/:lsAgentId',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-detail',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lifesciences*',
                            name: 'ai-ls-page',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        }
                    ],
                    beforeEnter(to, from, next) {
                        location.href = `/ls/#${to.fullPath}`
                        next(false)
                    },
                },
                {
                    name: 'workflow',
                    path: '/material/workflow',
                    component: () => import('@/pages/material/workflow/index.vue'),
                },
                {
                    name: 'substance',
                    path: '/material/substance',
                    component: () => import('@/pages/material/substance/index.vue'),
                },
                {
                    name: 'property',
                    path: '/material/property',
                    component: () => import('@/pages/material/property/index.vue'),
                },
                {
                    name: 'structure',
                    path: '/material/structure',
                    component: () => import('@/pages/material/structure/index.vue'),
                },
                {
                    name: 'metal',
                    path: '/material/metal',
                    component: () => import('@/pages/material/metal/index.vue'),
                },
                {
                    name: 'grade',
                    path: '/material/grade/:grade_id',
                    component: () => import('@/pages/material/grade/index.vue'),
                },
                {
                    name: 'mse-doc-analyzer',
                    path: '/material/analyzer',
                    component: () => import('@/pages/material/doc-analyzer/MSEDocAnalyzer.vue'),
                },
                {
                    name: 'mse-doc-analyzer-result',
                    path: '/material/analyzer/:lsAgentId',
                    component: () => import('@/pages/material/doc-analyzer/MSEDocAnalyzer.vue'),
                },
                {
                    name: 'userSetting',
                    path: '/user-setting',
                    component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Layout.vue'),
                    beforeEnter: async(to, from, next) => {
                        if (to.name === 'userSettingPackage') {
                            try {
                                const homeService = injectService<HomeService>(HomeService);
                                const data = await homeService.getHomePageType();
                                return next({
                                    name: `userSettingPackage${data.homepage_type === 'RD_PLG' ? 'RD' : data.homepage_type}`,
                                    query: to.query,
                                })
                            } catch (error) {
                                return next({
                                    name: 'userSettingPackageRD',
                                    query: to.query,
                                })
                            }
                        }
                        next();
                    },
                    children: [
                        {
                            path: 'amount',
                            name: 'userSettingAmount',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Amount.vue'),
                        },
                        {
                            path: 'package',
                            name: 'userSettingPackage',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Package.vue'),
                        },
                        {
                            path: 'package/management',
                            name: 'userSettingPackageManagement',
                            component: () => import(/* webpackChunkName: "air-usersetting-management" */'@/pages/user-setting/new-version/product-lines/Management.vue'),
                        },
                        {
                            path: 'package/rd',
                            name: 'userSettingPackageRD',
                            component: () => import(/* webpackChunkName: "air-usersetting-RD" */'@/pages/user-setting/new-version/product-lines/RD.vue'),
                        }, {
                            path: 'package/ip',
                            name: 'userSettingPackageIP',
                            component: async() => {
                                const data = await accountDeferred.promise;
                                if (data.siteRegion === SiteRegion.CN) {
                                    return import(/* webpackChunkName: "air-usersetting-IP" */'@/pages/user-setting/new-version/product-lines/IP.vue')
                                }
                                return import(/* webpackChunkName: "air-usersetting-IpPlg" */'@/pages/user-setting/new-version/product-lines/IpPlg.vue')
                            },
                        }, {
                            path: 'package/mse',
                            name: 'userSettingPackageMSE',
                            component: () => import(/* webpackChunkName: "air-usersetting-MSE" */'@/pages/user-setting/new-version/product-lines/MSE.vue'),
                        }, {
                            path: 'package/ls',
                            name: 'userSettingPackageLS',
                            component: () => import(/* webpackChunkName: "air-usersetting-LS" */'@/pages/user-setting/new-version/product-lines/LS.vue'),
                            beforeEnter(to, from, next) {
                                location.href = `/ls/#${to.fullPath}`
                                next(false)
                            },
                        }, {
                            path: 'package/ls/plg',
                            name: 'userSettingPackageLS_PLG',
                            component: () => import(/* webpackChunkName: "air-usersetting-LSPlg" */'@/pages/user-setting/new-version/product-lines/LsPlg.vue'),
                            beforeEnter(to, from, next) {
                                location.href = `/ls/#${to.fullPath}`
                                next(false)
                            },
                        }, {
                            path: 'agent-settings',
                            name: 'userSettingAgentSettings',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/AgentSettings.vue'),
                        }
                    ],
                },
                {
                    name: 'CompetitiveLandscape',
                    path: '/competitive-landscape',
                    component: () => import('@/pages/competitive-landscape/index.vue'),
                },
                {
                    name: 'CompanySearch',
                    path: '/company-search',
                    component: () => import('@/pages/company-search/index.vue'),
                },
                {
                    name: 'CompanyScout',
                    path: '/company-scout',
                    component: () => import('@/pages/company-scout/index.vue'),
                },
                {
                    name: 'CompanyScoutDetail',
                    path: '/company-scout/:company_scout_id',
                    component: () => import('@/pages/company-scout/detail.vue'),
                },
                {
                    name: 'ai-search',
                    path: '/ai-search',
                    component: () => import('@/pages/ai-search/index.vue'),
                    // component: () => import('@/pages/home/new-home.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                {
                    name: 'AiSearchResult',
                    path: '/ai-search/:conversation_id',
                    component: () => import('@/pages/ai-search/detail.vue'),
                    // 权限检查已移至组件内部，避免阻塞页面导航
                },
                {
                    path: '/403',
                    component: () => import('@/pages/403/Unauthorized.vue'),
                },
                {
                    path: '/404',
                    component: () => import('@/pages/404/NotFound.vue'),
                }
            ],
        },
        {
            name: 'onboarding',
            path: '/onboarding',
            component: () => import('@/pages/Onboarding.vue'),
        },
        {
            path: '/ai',
            name: 'ai',
            redirect: '/home',
            component: () => import('@/pages/Layout.vue'),
            meta: {
                navModuleType: NAV_MENU_TYPE.AI_GENERATE,
            },
            children: [
                {
                    path: '/ai/agent',
                    name: 'ai-agent',
                    redirect: '/home',
                },
                {
                    path: '/ai/agent-flow/:agent_job_id',
                    name: 'ai-agent-flow',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.agent_job_id as string,
                            data_type: E_ShareType.AI_AGENT,
                        });
                        next();
                    },
                    component: () => import('@/pages/ai/AgentFlow.vue'),
                }
            ],
        },
        ...proxyRouterList,
        // 通配符路由 - 捕获所有未匹配的路径并重定向到404页面
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            redirect: '/404',
        }
    ],
});

// 各产品首页路由配置。 如要修改，请同步修改/packages/air/src/main.ts的detailPageRegExps数组 ！！！
const router_map: Record<HomePageType, string> = {
    RD: 'rd',
    RD_PLG: 'home',
    LS: 'ls',
    MSE: 'mse',
    IP: 'ip',
    LS_PLG: 'ls/home',
    HOME: 'home',
}

router.beforeEach(async(to, from, next) => {
    if (['/', '', '/home', '/home/'].includes(to.path)) {
        try {
            if (to.query.ai_search_text) {
                next()
                return;
            }
            const isGuest = !await isUserLogin();
            if (isGuest) {
                next({ name: 'rd-home', query: to.query, hash: to.hash })
                return;
            }
            await addTrialDeferred.promise;

            const homeService = injectService<HomeService>(HomeService);
            const data = await homeService.getHomePageType();
            if (data.homepage_type && data.homepage_type !== 'RD_PLG' && data.homepage_type !== 'RD') {
                // next(`/${router_map[data.homepage_type]}`);
                window.location.href = `/${router_map[data.homepage_type]}`;
            } else {
                next({ name: 'rd-home', query: to.query, hash: to.hash });
            }
        } catch (error) {
            next()
        }
    } else if (Object.values(router_map).map(ele => `/${ele}`).includes(to.path)) {
        // 游客版跳转旧首页(/home);
        const isGuest = !await isUserLogin();
        if (isGuest) {
            next();
        }
    } else {
        next()
    }
});

export default router;
